import {
	AttachmentId,
	defaultAttachmentTypeLabelMap,
	FileId,
	FileType,
	UploadedFile,
} from "@inrev/common";
import { FileUploadStatus, resolveFileUploadStatus } from "@portal/api/files";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { HiOutlineEye } from "react-icons/hi";
import {
	HiOutlineArrowDownTray,
	HiOutlineCheck,
	HiOutlineTrash,
	HiOutlineXMark,
	HiXMark,
} from "react-icons/hi2";
import { type AttachmentContext, trackAttachmentAction } from "../../analytics";
import { FileUploadStatusIndicator } from "../features/file-upload";
import { ConfirmationModalClickWrapper } from "../layout/ConfirmationModalClickWrapper";
import { cn } from "../lib/utils";
import { type PDFViewerControl } from "../patterns/pdf-viewer";
import { Button, Icon } from "../primitives";
import { trackingAttrs } from "../utils";
import { AttachmentTypeMultiSelect } from "./AttachmentTypeMultiSelect";
import { CustomFileIcon } from "./CustomFileIcon";
import { PDFViewerModal } from "./PDFViewerModal";
import { Tag } from "./Tag";
import { TagActionButton } from "./TagActionButton";

type FileUploadItemProps<TAllowedFileType extends FileType, TFileType extends string> = Omit<
	UploadedFile,
	"id" | "types"
> & {
	id: FileId | AttachmentId;
	types: TAllowedFileType[] | TFileType[];
	typeLabelMap: Partial<Record<TAllowedFileType | TFileType, string>>;
	allowDelete?: boolean | undefined;
	maxTypesPerFile?: number;
	expandedByDefault?: boolean;
	enableTypeSelection?: boolean;
	allowTypeConfirmation?: boolean;
	createdAtMs?: number;
	baseUrl?: string;
	queryKey?: string;
	enablePDFViewer?: boolean;
	onTypeConfirmation?: () => void;
	onTypeConfirmationCancel?: () => void;
	onDelete?: (id: string) => void;
	onDownload?: (id: string) => void;
	onViewSummary?: () => void;
	onSave?: (file: UploadedFile<TAllowedFileType>, newTypes: TAllowedFileType[]) => void;
	onBlur?: () => void;
	deleting?: boolean;
	analyticsContext: AttachmentContext;
} & (
		| {
				allowDelete?: false | undefined;
		  }
		| {
				allowDelete: true;
				onDelete: (id: string) => void;
				deleting: boolean;
		  }
	);

export const FileUploadItem = <TAllowedFileType extends FileType, TFileType extends string>({
	id,
	name,
	extension,
	typesConfirmedByAdmin = false,
	types,
	classificationStatus,
	typeLabelMap,
	allowDelete,
	expandedByDefault = false,
	enableTypeSelection,
	allowTypeConfirmation,
	baseUrl,
	queryKey,
	createdAtMs,
	enablePDFViewer = true,
	onTypeConfirmation,
	onTypeConfirmationCancel,
	onDelete,
	onDownload,
	onViewSummary,
	onSave,
	onBlur,
	deleting,
	analyticsContext,
}: FileUploadItemProps<TAllowedFileType, TFileType>) => {
	const [open, setOpen] = useState(false);
	const typeLabelMapKeys = Object.keys(typeLabelMap);
	const [openPDFViewer, setOpenPDFViewer] = useState(false);
	const defaultWithCustomTypeMap = {
		...defaultAttachmentTypeLabelMap,
		...typeLabelMap,
	};
	const showSuggestedTypes = useMemo(
		() => allowTypeConfirmation && types.length > 0,
		[allowTypeConfirmation, types],
	);
	const viewerControl = useMemo<PDFViewerControl | undefined>(
		() =>
			baseUrl && queryKey
				? { fetchUrl: `${baseUrl}/${id}`, queryKey: [queryKey, id, true] }
				: undefined,
		[baseUrl, queryKey, id],
	);

	const uploadStatus = resolveFileUploadStatus({
		classificationStatus,
		hasTypes: types.length > 0,
		typesConfirmedByAdmin,
	});
	const showClassificationIndicator = types.length === 0 || showSuggestedTypes;

	const handleSave = (selectedTypes: TAllowedFileType[] | TFileType[]) => {
		!!onSave &&
			onSave(
				{
					id: id as FileId,
					name,
					extension,
					typesConfirmedByAdmin,
					types: types as TAllowedFileType[],
					classificationStatus,
				},
				selectedTypes as TAllowedFileType[],
			);
	};

	// card-consolidation: the wrapper <div> below is a card surface — replace with <Card elevation="raised"> from primitives
	return (
		<div className="relative flex flex-col p-[15px] bg-white border border-gray-300/75 rounded-[5px] shadow-sm text-[15px] text-primary items">
			<div className="min-h-[50px] break-words flex space-x-[25px]">
				<CustomFileIcon extension={extension} />
				<div className="flex-1 flex flex-col space-y-[2px]">
					<span className="w-full flex justify-between">
						<div
							data-amp-mask
							data-sentry-mask
							className="flex gap-6 mr-20 font-[435] text-[14.5px] items-center"
						>
							{name}.{extension}
							{showClassificationIndicator && (
								<FileUploadStatusIndicator status={uploadStatus} id={id} />
							)}
						</div>
						<div className="w-fit min-w-fit h-fit flex items-center gap-[2px] absolute right-[15px] top-[10px]">
							{allowDelete && (
								<ConfirmationModalClickWrapper
									message={
										<div className="w-[375px] text-center break-words">
											Are you sure you want to delete
											<br />
											<span data-amp-mask data-sentry-mask>
												{name}.{extension}?
											</span>
										</div>
									}
									className="items-center"
									confirmButtonText="Delete"
									confirmationButtonColor="danger"
									confirmTrackingId={`${analyticsContext}-attachment-delete-confirm`}
									cancelTrackingId={`${analyticsContext}-attachment-delete-cancel`}
									onConfirm={() => {
										trackAttachmentAction("delete", analyticsContext);
										onDelete(id);
									}}
								>
									<div
										{...trackingAttrs(`${analyticsContext}-attachment-delete`)}
										title="Delete File"
										data-testid="delete-file-button"
										className="cursor-pointer p-[4px] text-red-400 hover:text-red-500"
									>
										<HiOutlineTrash className="text-[17px]" />
									</div>
								</ConfirmationModalClickWrapper>
							)}
							{extension === "pdf" && !!onDownload && enablePDFViewer && (
								<Button
									title="View PDF"
									variant="ghost"
									onClick={() => {
										trackAttachmentAction("preview", analyticsContext);
										setOpenPDFViewer(true);
									}}
									className="cursor-pointer p-[4px] text-secondary/75 hover:text-gray-800"
									data-testid="view-pdf-button"
									trackingId={`${analyticsContext}-attachment-preview`}
								>
									<HiOutlineEye className="text-[18px] stroke-[1.6px]" />
								</Button>
							)}
							{!!onViewSummary && (
								<Button
									title="View Summary"
									variant="ghost"
									onClick={() => {
										trackAttachmentAction("view_summary", analyticsContext);
										onViewSummary();
									}}
									className="cursor-pointer p-[4px] text-secondary/75 hover:text-gray-800"
									data-testid="view-summary-button"
									trackingId={`${analyticsContext}-attachment-summary-preview`}
								>
									<Icon type="document-chart-bar" size="small" />
								</Button>
							)}
							{!!onDownload && (
								<Button
									title="Download File"
									variant="ghost"
									onClick={() => {
										trackAttachmentAction("download", analyticsContext);
										onDownload(id);
									}}
									className="cursor-pointer p-[4px] text-secondary/75 hover:text-gray-800"
									data-testid="download-file-button"
									trackingId={`${analyticsContext}-attachment-download`}
								>
									<HiOutlineArrowDownTray className="text-[18px]" />
								</Button>
							)}
						</div>
					</span>
					{createdAtMs && (
						<span className="text-tertiary text-[12px]">
							{DateTime.fromMillis(createdAtMs).toFormat("LLLL dd, yyyy")}
						</span>
					)}
					{!showSuggestedTypes && (
						<div
							className={cn(
								"pt-4 flex flex-row gap-2 flex-wrap pr-[12px] pb-[6px]",
								types.length > 0 && "animate-file-tag-pop-in",
							)}
						>
							{types.map((type, idx) => (
								<Tag key={`${name}-${type}-${idx}`}>{defaultWithCustomTypeMap[type]}</Tag>
							))}
							{enableTypeSelection && !typesConfirmedByAdmin && (
								<TagActionButton
									iconOnly={types.length > 0}
									onClick={() => {
										setOpen(true);
									}}
								/>
							)}
							{typesConfirmedByAdmin && !!allowTypeConfirmation && (
								<TagActionButton
									iconOnly={types.length > 0}
									onClick={() => {
										setOpen(true);
									}}
								/>
							)}
						</div>
					)}
					{showSuggestedTypes && typesConfirmedByAdmin && (
						<div className="pt-4 flex flex-row gap-2 flex-wrap pr-[12px] pb-[6px]">
							{types.length > 0 &&
								types.map((type, idx) => (
									<Tag key={`${name}-${type}-${idx}`}>{defaultWithCustomTypeMap[type]}</Tag>
								))}
							<TagActionButton
								iconOnly={types.length > 0}
								onClick={() => {
									setOpen(true);
								}}
							/>
						</div>
					)}
				</div>
			</div>
			{showSuggestedTypes && !typesConfirmedByAdmin && (
				<div className="w-full bg-gray-100 pl-3 pt-2 pb-3 pr-2 rounded-md border-dashed border-2 border-gray-400 flex flex-col gap-[5px] mt-[4px]">
					<div className="flex justify-between w-full text-[11.5px] font-medium items-center">
						Agent Suggested Types
						<div className="flex gap-[10px]">
							<button
								type="button"
								onClick={onTypeConfirmationCancel}
								className="h-6 w-6 flex items-center justify-center rounded-full bg-gray-300 text-center hover:bg-gray-400"
							>
								<HiOutlineXMark className="text-gray-700 stroke-[3px]" />
							</button>
							<button
								type="button"
								data-testid="confirm-suggested-file-types"
								onClick={onTypeConfirmation}
								className="h-6 w-6 flex items-center justify-center rounded-full bg-brand-accent hover:bg-[#2c5aa0]"
							>
								<HiOutlineCheck className="text-inverse stroke-[3px]" />
							</button>
						</div>
					</div>
					<div className="flex flex-row gap-2 flex-wrap">
						{types.map((type, idx) => (
							<Tag key={`${name}-${type}-${idx}`}>{defaultWithCustomTypeMap[type]}</Tag>
						))}
						<TagActionButton
							iconOnly={types.length > 0}
							onClick={() => {
								setOpen(true);
							}}
						/>
					</div>
				</div>
			)}
			{deleting && (
				<div className="absolute top-0 right-0 bottom-0 left-0 rounded-[5px] bg-striped bg-white bg-opacity-80 !m-0"></div>
			)}
			{open && (
				<AttachmentTypeMultiSelect
					availableTypes={typeLabelMapKeys as TAllowedFileType[]}
					initialTypes={types as TAllowedFileType[]}
					onSave={handleSave}
					expandedByDefault={expandedByDefault}
					title={"Select File Type"}
					close={() => setOpen(false)}
					onBlur={onBlur}
				/>
			)}
			{openPDFViewer && viewerControl != null && (
				<PDFViewerModal
					onClose={() => setOpenPDFViewer(false)}
					control={viewerControl}
					header={name}
					trackingId={`${analyticsContext}-attachment-viewer`}
				/>
			)}
		</div>
	);
};

export const PendingFileUploadItem = <TAllowedFileType extends FileType, TFileType extends string>({
	name,
	extension,
}: Omit<
	FileUploadItemProps<TAllowedFileType, TFileType>,
	"onDelete" | "deleting" | "onDownload" | "typesConfirmedByAdmin" | "analyticsContext"
>) => (
	// card-consolidation: the wrapper <div> below is a card surface — replace with <Card elevation="raised"> from primitives
	<div className="opacity-60 min-h-[50px] break-words flex items-center space-x-[25px] p-[15px] bg-white border border-gray-200 rounded-[5px] shadow-sm text-[15px] text-primary">
		<CustomFileIcon extension={extension} />
		<div className="flex-1 flex flex-col space-y-[2px]">
			<span className="flex items-center gap-2">
				{name}.{extension}
				<FileUploadStatusIndicator
					status={FileUploadStatus.uploading}
					id={`pending-${name}.${extension}`}
				/>
			</span>
		</div>
		<HiXMark className="text-[19px] opacity-0" />
	</div>
);
